import React, { useState } from 'react';
import { BuildingOffice2Icon, GlobeAltIcon, UserGroupIcon, UsersIcon } from '@heroicons/react/24/outline';
import { RowsSkeleton } from './DashboardSkeleton';
import { Avatar, Card, EmptyState, ListRow, SectionHeader, TextButton } from './ui';
import { valueTone } from './theme';
import { formatInteger, formatMoney, formatPercent } from './format';
import { typeLabel } from './periodText';

const COLLAPSED_ROWS = 5;

const TITLES = {
  country: { key: 'byCountry', fallback: 'By country', icon: GlobeAltIcon },
  city: { key: 'byCity', fallback: 'By city', icon: BuildingOffice2Icon },
  'super-agent': { key: 'bySuperAgent', fallback: 'By super agent', icon: UserGroupIcon },
  agent: { key: 'byAgent', fallback: 'By agent', icon: UserGroupIcon },
  player: { key: 'byPlayer', fallback: 'By player', icon: UsersIcon }
};

const MIXED_TITLE = { key: 'byDirectReport', fallback: 'By direct report', icon: UserGroupIcon };

/** GGR per direct report (countries for an admin, players for an agent), with a drill into Reports. */
export default function NetworkBreakdownCard({ t, currency, rows, periodName, onSelect, className = '' }) {
  const [expanded, setExpanded] = useState(false);
  const types = new Set((rows || []).map((row) => row.type));
  const title = (types.size === 1 && TITLES[[...types][0]]) || MIXED_TITLE;
  const header = <SectionHeader icon={title.icon} title={t(title.key, title.fallback)} subtitle={periodName} />;

  if (!rows) {
    return (
      <Card className={className}>
        {header}
        <RowsSkeleton />
      </Card>
    );
  }

  const active = rows.filter((row) => row.active);
  const visible = expanded ? active : active.slice(0, COLLAPSED_ROWS);
  const mixed = types.size > 1;

  return (
    <Card className={className}>
      {header}
      {active.length === 0 ? (
        <EmptyState title={t('noActivity', 'No activity in this period')} />
      ) : (
        <div className="-mx-2">
          {visible.map((row) => (
            <ListRow
              key={row.username}
              onClick={() => onSelect(row)}
              leading={<Avatar name={row.name} seed={row.username} />}
              label={row.name}
              hint={mixed ? typeLabel(t, row.type) : null}
              aside={row.share !== null ? formatPercent(row.share, 0) : null}
              value={formatMoney(row.ggr, currency)}
              valueClassName={valueTone(row.ggr)}
            />
          ))}
        </div>
      )}
      {active.length > COLLAPSED_ROWS && (
        <TextButton onClick={() => setExpanded((value) => !value)} aria-expanded={expanded} className="mt-1">
          {expanded ? t('showLess', 'Show less') : t('showAll', 'Show all ({{count}})', { count: formatInteger(active.length) })}
        </TextButton>
      )}
    </Card>
  );
}
